import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ShieldX, ArrowLeft, Home } from 'lucide-react';

/**
 * AccessDenied component - displays when user lacks permission to access a page
 *
 * @param {string} title - Optional custom title (default: "Access Denied")
 * @param {string} message - Optional custom message
 * @param {string} backTo - Optional specific path to navigate back to
 * @param {string} backLabel - Optional label for back button
 */
export function AccessDenied({
  title = "Access Denied",
  message = "You don't have permission to access this page.",
  backTo,
  backLabel
}) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-md w-full overflow-hidden">
        {/* Header with icon */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 px-8 pt-10 pb-8 text-center border-b border-gray-100">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
            <ShieldX className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        </div>

        {/* Content */}
        <div className="px-8 py-6">
          <p className="text-gray-600 text-center leading-relaxed">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="px-8 pb-8 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backLabel || "Go Back"}
          </Button>
          <Button
            className="flex-1"
            onClick={handleDashboard}
          >
            <Home className="h-4 w-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
